import { createContext, useContext } from 'react';

import { Disclaimer } from './Disclaimer';
import { LtcPayoutSummary } from './LtcPayoutSummary';
import { DeathBenefit } from './DeathBenefit';
import { TotalInvestedIntoPolicy } from './TotalInvestedIntoPolicy';
import { PolicyPayoutCalculation } from './PolicyPayoutCalculation';
import { buildPolicyInsightData, type PolicyInsightData } from './policyInsightUtils';

import { useSelector } from 'react-redux';
import { selectClient } from '../../model';
import { getPolicyNameParts } from '@shared';

const PolicyInsightContext = createContext<
  | {
      ltcPolicy: PolicyFundingSource;
      phaseCalculations: SinglePhaseCalculations[];
      insightData: PolicyInsightData;
      fundingSourceId: string;
    }
  | undefined
>(undefined);

export function usePolicyInsightContext() {
  const context = useContext(PolicyInsightContext);
  if (!context) {
    throw new Error('usePolicyInsightContext must be used within PolicyInsightProvider');
  }
  return context;
}

export function PolicyInsight({ fundingSourceId }: { fundingSourceId: string }) {
  const client = useSelector(selectClient);
  const { policyPhaseCalculations, multipleFundingSources } = client;

  const ltcPolicy = multipleFundingSources.ltcPolicy[fundingSourceId];
  const phaseCalculations = policyPhaseCalculations[fundingSourceId];

  if (!ltcPolicy || !phaseCalculations) {
    return <div className="p-4 text-center text-gray-700">Policy data not available</div>;
  }

  const insightData = buildPolicyInsightData(client, phaseCalculations, ltcPolicy);

  const { primary: policyName, secondary: policyTypeLabel } = getPolicyNameParts(
    ltcPolicy,
    'Insurance Policy',
  );

  return (
    <PolicyInsightContext.Provider
      value={{ ltcPolicy, phaseCalculations, insightData, fundingSourceId }}
    >
      <div className="flex flex-col gap-8 pb-8">
        <Disclaimer />
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-950">Funding Type</h2>
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="font-medium text-gray-950">{policyName}</p>
            {policyTypeLabel && (
              <p className="text-sm text-muted-foreground">{policyTypeLabel}</p>
            )}
          </div>
        </section>
        <TotalInvestedIntoPolicy />
        <LtcPayoutSummary />
        <DeathBenefit />
        <PolicyPayoutCalculation />
      </div>
    </PolicyInsightContext.Provider>
  );
}
